// src/utils/arcaErrorHelper.js
// Helper central para mapear cualquier respuesta/error de ARCA (vía
// TusFacturas, directo, o cualquier intermediario) a un objeto normalizado
// que sirve para mostrar en un <Alert>, un toast, un modal, etc.
//
// Esta capa es la única que se debe usar en el FE para errores de
// facturación. Reemplaza:
//   - getInvoiceUserMessage() en InvoicePreviewModal.jsx
//   - El regex hardcoded en FacturarCuentaAbiertaModal.jsx
//   - El message.error() genérico en PartialCreditModal.jsx (notas de crédito)
//
// Uso:
//   import { mapResponseToAlert, mapErrorToAlert } from '../../utils/arcaErrorHelper';
//
//   // Caso 1: el backend devolvió un response (success/queued/error)
//   const t = mapResponseToAlert(responseData);
//   if (t) setInvoiceResponse(t);
//
//   // Caso 2: el fetch falló con un error
//   const t = mapErrorToAlert(err);
//   if (t) setError(t);
//
// Shape retornado:
//   {
//     type: 'success' | 'info' | 'warning' | 'error',
//     color: 'success' | 'info' | 'warning' | 'error',
//     title: string,            // título corto
//     mensaje: string,          // descripción user-friendly
//     accion: string | null,    // qué hacer
//     code: string | null,      // código del error (ARCA-10243, etc.)
//     tecnico: string,          // mensaje crudo, limpio del intermediario
//     matched: boolean          // true si lo reconocimos en el catálogo
//   }

import { traducirErrorARCA } from './arcaErrorTranslator';

/**
 * Mapea la respuesta del backend (POST /invoices/v2/create o /credit-notes)
 * a un objeto listo para mostrar.
 *
 * Devuelve `null` si la respuesta es un éxito puro (no es un error).
 */
export function mapResponseToAlert(response) {
    if (!response) return null;

    // Éxito: el comprobante se emitió con CAE
    if (response.success && response.invoice && !response.queued) {
        return {
            type: 'success',
            color: 'success',
            title: '¡Comprobante emitido!',
            mensaje: `N° comprobante: ${response.invoice.numero_comprobante || response.invoice.comprobante_nro || ''}`,
            accion: null,
            code: null,
            tecnico: '',
            matched: true
        };
    }

    // Encolado: AFIP estaba caído, el sistema lo va a reintentar
    if (response.queued) {
        return {
            type: 'info',
            color: 'info',
            title: 'Comprobante en espera',
            mensaje:
                'El comprobante se guardó y se procesará automáticamente cuando ARCA esté disponible.',
            accion: 'Revisá el menú "Facturas en Cola" para ver el estado.',
            code: null,
            tecnico: '',
            matched: true
        };
    }

    // Error: usar el traductor
    const rawError =
        response.error ||
        response.message ||
        response.detalles ||
        'Error desconocido';
    const t = traducirErrorARCA(rawError);

    return {
        type: 'error',
        color: 'error',
        title: t.title,
        mensaje: t.mensaje,
        accion: t.accion,
        code: t.code,
        tecnico: t.tecnico,
        matched: t.matched
    };
}

/**
 * Mapea un error de fetch/axios a un objeto listo para mostrar.
 */
export function mapErrorToAlert(err) {
    if (!err) return null;
    const data = err.response?.data;
    const raw = data?.error || data?.message || data?.detalles || err.message || 'Error desconocido';
    const t = traducirErrorARCA(raw);
    return {
        type: 'error',
        color: 'error',
        title: t.title,
        mensaje: t.mensaje,
        accion: t.accion,
        code: t.code,
        tecnico: t.tecnico,
        matched: t.matched
    };
}

/**
 * Convierte un objeto alert a un string multi-línea para mostrar
 * en componentes que no aceptan JSX (e.g. message.error de antd).
 */
export function alertToString(alert) {
    if (!alert) return '';
    const lines = [];
    if (alert.title) lines.push(alert.title);
    if (alert.mensaje) lines.push(alert.mensaje);
    if (alert.accion) lines.push(`→ ${alert.accion}`);
    if (alert.code) lines.push(`(código: ${alert.code})`);
    return lines.join('\n');
}
